import {
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnInit,
  Output,
  SimpleChanges,
} from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { ICountry, IMapMarker, IUserAPIResponse } from '@boma/client-shared';
import {
  ILocation,
  IProfileEntityDetail,
  LocationUsageType,
} from '@boma/supplier-portal/shared/entities';
import {
  CountriesService,
  HelperFunctionsService,
} from '@boma/supplier-portal/shared/services';
import { getSelectedOrganizationInfo } from '@boma/supplier-portal/shared/state';
import { Store } from '@ngrx/store';
import { take } from 'rxjs/operators';

@Component({
  selector: 'boma-location-form',
  templateUrl: './boma-location-form.component.html',
  styleUrls: ['./boma-location-form.component.scss'],
})
export class BomaLocationFormComponent implements OnInit, OnChanges {
  countries: ICountry[];

  defaultOpeningTime = new Date(1, 1, 1, 8, 0, 0);
  defaultClosingTime = new Date(1, 1, 1, 17, 0, 0);

  isSubmitting = false;
  timer: any;

  selectedValues: string[] = [];

  binary = [
    { name: 'Location is Enabled', value: true },
    { name: 'Location is Disabled', value: false },
  ];

  markers: IMapMarker[] = [];
  currentOrganization: IProfileEntityDetail;
  @Input() country: string;
  @Input() fullWidth = false;
  @Input() customerLocation = false;
  @Input() newLocation = false;

  @Input() location: ILocation;

  @Output() saved = new EventEmitter<ILocation>();
  locationFormGroup: FormGroup = new FormGroup({
    id: new FormControl(''),
    countryId: new FormControl('', Validators.required),
    name: new FormControl('', Validators.required),
    active: new FormControl(true, Validators.required),
    isEntireCountry: new FormControl(true, Validators.required),
    distributionBoundary: new FormControl(null),
    point: new FormControl(null, Validators.required),
    openingTime: new FormControl('08:00'),
    closingTime: new FormControl('17:00'),
    notificationEmailAddress: new FormControl('', Validators.required),
    contactPersonName: new FormControl('', Validators.required),
    contactPersonNumber: new FormControl('', Validators.required),
    usageType: new FormControl(LocationUsageType.Dispatch, Validators.required),
    isHeadOffice: new FormControl(false, Validators.required),
    address: new FormControl(null, Validators.required),
  });

  editLocation = false;

  selectedUser: IUserAPIResponse = null;

  selectedUsageType: LocationUsageType = LocationUsageType.Receiving;
  locationTypes = [
    {
      label: 'POS / Shop',
      description: 'A physical shop that makes sales to public.',
      value: LocationUsageType.Sales,
    },
    {
      label: 'Stock with or without Online Orders',
      description:
        'Set up this location to carry stock that can be either ordered internally or externally, via the mobile application or web store.',
      value: LocationUsageType.Dispatch,
    },
    {
      label: 'POS & Online Orders',
      description:
        'This location will be used to process online orders and make POS sales.',
      value: LocationUsageType.SalesAndDispatch,
    },
  ];

  constructor(
    private store: Store,
    private countrySvc: CountriesService,
    private helperService: HelperFunctionsService
  ) {}

  ngOnInit(): void {
    this.store
      .select(getSelectedOrganizationInfo)
      .pipe(take(1))
      .subscribe((org) => {
        if (org) {
          this.currentOrganization = org as IProfileEntityDetail;
        }
      });

    this.getAndSetCountries();

    if (this.location) {
      this.patchLocationForm(this.location);
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.location?.currentValue) {
      this.patchLocationForm(changes.location.currentValue as ILocation);
    }
  }

  private patchLocationForm(location: ILocation): void {
    this.locationFormGroup.patchValue({
      ...location,
      openingTime: this.trimTime(location.openingTime, '08:00'),
      closingTime: this.trimTime(location.closingTime, '17:00'),
      usageType: this.resolveUsageTypeValue(location.usageType),
      point: location.point,
    });

    if (location.point?.coordinates?.length >= 2) {
      this.markers = [
        {
          long: location.point.coordinates[0],
          lat: location.point.coordinates[1],
        },
      ];
    }

    this.resolveUsageType(location);
  }

  private trimTime(value: string, fallback: string): string {
    return value ? value.substring(0, 5) : fallback;
  }

  private normalizeTime(value: string, fallback: string): string {
    const time = this.trimTime(value, fallback);
    return `${time}:00`;
  }

  private resolveUsageTypeValue(usageType: LocationUsageType | string): LocationUsageType {
    if (typeof usageType === 'number') {
      return usageType;
    }

    const value = LocationUsageType[usageType as keyof typeof LocationUsageType];
    return typeof value === 'number' ? value : LocationUsageType.Dispatch;
  }

  private resolveUsageType(location: ILocation): void {
    if (location?.usageType) {
      const usageType = location.usageType.toString();
      switch (usageType) {
        case 'SalesAndDispatch':
          this.selectedValues = ['pos', 'orders'];
          break;
        case 'Sales':
          this.selectedValues = ['pos'];
          break;
        case 'Dispatch':
          this.selectedValues = ['orders'];
          break;
        case 'Production':
          this.selectedValues = ['production'];
          break;
      }
    }
  }

  async getAndSetCountries() {
    this.countrySvc.loadCountries().subscribe((countries) => {
      this.countries = countries;
      this.countries.map((c) => {
        if (c.name === this.currentOrganization.country) {
          this.locationFormGroup.patchValue({
            countryId: c.id,
          });
        }
      });
    });
    // console.log(this.countries);
    // console.log(this.currentOrganization);
  }

  handleSetPolygon(polygon) {
    const geoObject: { coordinates: any[][] } = { coordinates: [[]] };

    geoObject.coordinates[0][0] = polygon ? polygon : [];
    this.locationFormGroup.patchValue({
      distributionBoundary: geoObject,
      isEntireCountry: !polygon ? true : false,
    });
    this.location = this.locationFormGroup.value as ILocation;
  }

  handleSetPoint(location) {
    const tempAddress = this.helperService.sanitizeParams(location.address);
    this.locationFormGroup.patchValue({
      point: { coordinates: location.point },
      address: {
        ...tempAddress,
      },
    });
  }

  handleClick(event) {
    this.locationFormGroup.patchValue({
      point: { coordinates: event?.point },
      address: event?.address || this.location.address,
    });
    this.location = this.locationFormGroup.value as ILocation;
  }

  handleSelectedUser(ev) {
    this.selectedUser = ev;
    //update the form
    //TODO This API is response needs to be improved it contains lots of duplicate data and no email
    this.locationFormGroup.patchValue({
      contactPersonName: this.selectedUser.user.fullName,
      notificationEmailAddress: this.selectedUser?.companyEmail,
      contactPersonNumber:
        this.selectedUser.companyContactNumber ||
        this.selectedUser.personalContactNumber,
    });
  }

  handleLocationUseChange() {
    console.log('Handle Location Usage Change');
    //default to receiving or sales and dispatch
    this.selectedUsageType = this.customerLocation
      ? LocationUsageType.Receiving
      : LocationUsageType.SalesAndDispatch;

    if (this.selectedValues.indexOf('production') > -1) {
      this.selectedValues = ['production'];
      this.selectedUsageType = LocationUsageType.Production;
    } else {
      if (
        this.selectedValues.indexOf('pos') === -1 &&
        this.selectedValues.indexOf('orders') === -1
      ) {
        this.selectedUsageType = LocationUsageType.Receiving;
      } else if (this.selectedValues.indexOf('orders') === -1) {
        this.selectedUsageType = LocationUsageType.Sales;
      } else if (this.selectedValues.indexOf('pos') === -1) {
        this.selectedUsageType = LocationUsageType.Dispatch;
      }
    }

    this.locationFormGroup.patchValue({
      usageType: this.selectedUsageType,
    });

    this.location = this.locationFormGroup.value as ILocation;
  }

  save() {
    this.isSubmitting = true;
    this.handleLocationUseChange();

    this.timer = setTimeout(() => {
      this.isSubmitting = false;
      clearTimeout(this.timer);
    }, 200);
    this.locationFormGroup = this.helperService.trimFormGroupValues(
      this.locationFormGroup
    );
    const formValue = this.locationFormGroup.value as ILocation;
    const location = {
      ...formValue,
      closingTime: this.normalizeTime(formValue.closingTime, '17:00'),
      openingTime: this.normalizeTime(formValue.openingTime, '08:00'),
    };

    this.saved.emit(location);

    //Restoring the time in case we have a submission issue.
    this.locationFormGroup.patchValue({
      ...location,
      openingTime: this.trimTime(location.openingTime, '08:00'),
      closingTime: this.trimTime(location.closingTime, '17:00'),
    });
  }
}
